/* global Stripe */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["mountPoint", "errorMessage", "submitButton", "submitText", "loadingText"]

  static values = {
    publishableKey:             String,
    token:                      String,  // signed server token — contains all payment params
    amount:                     Number,  // display only — authoritative amount is inside the token
    currency:                   String,
    saveCardMode:               String,
    paymentMethodConfiguration: String,  // optional — non-default Stripe payment method configuration ID
    createPaymentIntentUrl:     String,
    successRedirectUrl:         String,
  }

  async connect() {
    try {
      this.stripe = Stripe(this.publishableKeyValue)
    } catch (_e) {
      console.error("Stripe.js is not loaded")
      return
    }

    this.elements = this.stripe.elements({
      mode:     "payment",
      amount:   this.amountValue,
      currency: this.currencyValue,
      ...(this.paymentMethodConfigurationValue && { paymentMethodConfiguration: this.paymentMethodConfigurationValue }),
    })

    const paymentElement = this.elements.create("payment", {
      layout: "tabs",
      ...(this.saveCardModeValue === "optional" && { savePaymentMethod: true }),
    })

    paymentElement.mount(this.mountPointTarget)
  }

  async submit() {
    this.#setLoading(true)
    this.#clearError()

    // Step 1: validate the Payment Element and collect payment details
    const { error: submitError } = await this.elements.submit()
    if (submitError) {
      this.#showError(submitError.message)
      this.#setLoading(false)
      return
    }

    // Step 2: create the PaymentIntent on the server (amount/chargeable are inside the signed token)
    let clientSecret
    try {
      clientSecret = await this.#createPaymentIntent()
    } catch (err) {
      this.#showError(err.message)
      this.#setLoading(false)
      return
    }

    // Step 3: confirm the payment with Stripe
    const { error: confirmError, paymentIntent } = await this.stripe.confirmPayment({
      elements:      this.elements,
      clientSecret:  clientSecret,
      confirmParams: { return_url: this.successRedirectUrlValue },
      redirect:      "if_required",
    })

    if (confirmError) {
      this.#showError(confirmError.message)
      this.#setLoading(false)
    } else {
      // Non-redirect payment methods (e.g. card) resolve here instead of redirecting via
      // return_url — append the same query params Stripe would have added so the
      // confirmation page can look up the PaymentTransaction and create the order.
      const url = new URL(this.successRedirectUrlValue)
      url.searchParams.set("payment_intent", paymentIntent.id)
      url.searchParams.set("redirect_status", paymentIntent.status)
      window.location.href = url.toString()
    }
  }

  // --- private ---

  async #createPaymentIntent() {
    const response = await fetch(this.createPaymentIntentUrlValue, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      },
      body: JSON.stringify({ payment_token: this.tokenValue }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || "Could not initialise payment. Please try again.")
    }

    return data.client_secret
  }

  #setLoading(isLoading) {
    this.submitButtonTarget.disabled = isLoading
    this.submitTextTarget.classList.toggle("hidden", isLoading)
    this.loadingTextTarget.classList.toggle("hidden", !isLoading)
  }

  #showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  #clearError() {
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }
}

